import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppHeader } from '@/components/AppHeader';
import { getPublicStandings, getPublicTournament } from '@/lib/api';
import { usePolledData } from '@/lib/usePolledData';
import { ApiError } from '@/lib/api/core';
import { useAuth } from '@/contexts/AuthContext';
import { StandingsTable } from '@/components/standings/StandingsTable';
import type {
  StandingsResponse,
  Tournament,
} from '@swiss-magic/shared-types';

export function StandingsPage() {
  const { t } = useTranslation('tournament');
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { data, message, loading } = usePolledData<StandingsResponse>(
    () => getPublicStandings(slug!),
    [slug],
    {
      errorMapper: (_msg, error) =>
        error instanceof ApiError &&
        error.code === 'validation.standings.standings_unavailable'
          ? t('standings.not_yet')
          : null,
    },
  );
  // One-shot fetch so the sub-header (tournament name + intra-tournament
  // nav) stays visible even when the standings endpoint hasn't paid out
  // yet — pre round 1 it returns 404 and the data above stays null.
  const [tournament, setTournament] = useState<Tournament | null>(null);
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    getPublicTournament(slug)
      .then((t) => {
        if (!cancelled) setTournament(t);
      })
      .catch(() => {
        /* a missing slug is handled by the data fetch's error path */
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p>{t('loading')}</p>
      </div>
    );
  }

  const tournamentName = data?.tournamentName ?? tournament?.name;

  return (
    <div data-testid="standings-page" className="min-h-screen bg-background">
      <AppHeader />

      <main className="mx-auto max-w-4xl px-4 md:px-6 py-6">
        <div className="mb-6">
          {tournamentName && (
            <h2 className="text-xl md:text-2xl font-semibold mb-1">{tournamentName}</h2>
          )}
          <p className="text-sm text-muted-foreground mb-3">{t('standings.subtitle')}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to={`/t/${slug}`}
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.info')}
            </Link>
            <Link
              to={`/t/${slug}/crosstable`}
              data-testid="crosstable-link"
              className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              {t('common.section_labels.crosstable')}
            </Link>
          </div>
        </div>

        {message && (
          <p data-testid="standings-message" className="text-center text-muted-foreground">
            {message}
          </p>
        )}

        {data && <StandingsTable data={data} playerLinkSlug={slug} viewerUserId={user?.id} />}
      </main>
    </div>
  );
}
